/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import {
    ORGANIZATION_MANAGER_ERROR,
    ORGANIZATION_MANAGER_CLEAR_ERROR,
    ORGANIZATION_MANAGER_CLEAR_SUCCESS,
    ORGANIZATION_MANAGER_RESET,
    ORGANIZATION_MANAGER_SAVE_START,
    ORGANIZATION_MANAGER_SAVE_SUCCESS,
    ORGANIZATION_MANAGER_FETCH_START,
    ORGANIZATION_MANAGER_FETCH_SUCCESS,
    ORGANIZATION_MANAGER_DELETE_START,
    ORGANIZATION_MANAGER_DELETE_SUCCESS,
    OrganizationManagerAction
} from './OrganizationManagerActionsTypes';
import { convertToOrganizationManagerUser } from './OrganizationManagerConverter';
import { FetchOrganizationManagersDto, OrganizationManagerDto } from './OrganizationManagerDtos';
import { OrganizationManagerUser } from 'model/organizationManager';
import { showErrorThunk } from '@spike/notifications-action';

const organizationManagersUrl = '/organization_managers';

// Extract meaningful error message from API error response
const extractErrorMessage = (apiError: any): string => {
    // Handle Axios error structure
    if (apiError?.response?.data) {
        const { data } = apiError.response;

        // Check for validation errors (422 status) - direct field errors like {"name":["has already been taken"]}
        if (typeof data === 'object' && !data.message && !data.error && !data.errors) {
            // Look for field validation errors directly on data object
            const fieldNames = Object.keys(data);
            if (fieldNames.length > 0) {
                const firstField = fieldNames[0];
                const fieldError = data[firstField];
                if (Array.isArray(fieldError) && fieldError.length > 0) {
                    return `${firstField} ${fieldError[0]}`;
                }
                if (typeof fieldError === 'string') {
                    return `${firstField} ${fieldError}`;
                }
            }
        }

        // Check for validation errors in 'errors' property
        if (data.errors) {
            if (typeof data.errors === 'object') {
                // Extract first error from validation errors object
                const firstField = Object.keys(data.errors)[0];
                const firstError = data.errors[firstField];
                if (Array.isArray(firstError)) {
                    return firstError[0];
                }
                return firstError;
            }
            return data.errors;
        }

        // Check for general error message
        if (data.message) {
            return data.message;
        }

        // Check for error field
        if (data.error) {
            return data.error;
        }
    }

    // Fallback to Axios message or generic error
    if (apiError?.message) {
        return apiError.message;
    }

    // Final fallback
    return 'An unexpected error occurred. Please try again.';
};

const error = (err: string): OrganizationManagerAction => {
    return {
        type: ORGANIZATION_MANAGER_ERROR,
        payload: {
            error: err
        }
    };
};

export const clearOrganizationManagerError = (): OrganizationManagerAction => {
    return {
        type: ORGANIZATION_MANAGER_CLEAR_ERROR
    };
};

export const clearOrganizationManagerSuccess = (): OrganizationManagerAction => {
    return {
        type: ORGANIZATION_MANAGER_CLEAR_SUCCESS
    };
};

export const resetOrganizationManagerState = (): OrganizationManagerAction => {
    return {
        type: ORGANIZATION_MANAGER_RESET
    };
};

/**
 * Save Organization Manager - POST
 * Creates a new organization manager relationship
 */
export interface SaveOrganizationManagerPayload {
    organization_id: number;
    user_id: number;
    access_level_id: number;
    owner: boolean;
}

export const saveOrganizationManagerThunk = (
    payload: SaveOrganizationManagerPayload
): ThunkAction<Promise<OrganizationManagerUser>, RootState, null, OrganizationManagerAction> => {
    return async dispatch => {
        dispatch({ type: ORGANIZATION_MANAGER_SAVE_START });

        try {
            const url = `${organizationManagersUrl}`;
            const { data }: AxiosResponse<OrganizationManagerDto> = await ApiClient.post(url, payload);

            // Convert the DTO response to OrganizationManagerUser model
            const savedOrganizationManager = convertToOrganizationManagerUser(data);

            dispatch({
                type: ORGANIZATION_MANAGER_SAVE_SUCCESS,
                payload: {
                    organizationManager: savedOrganizationManager
                }
            });

            return savedOrganizationManager;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            // Return rejected promise instead of throwing
            return Promise.reject(apiError);
        }
    };
};

/**
 * Update Organization Manager - PATCH
 * Updates a person's role within one organization.
 *
 * Keyed on the membership id, not the user id. `owner` is round-tripped from
 * the loaded record so a role change cannot strip an owner designation.
 */
export interface UpdateOrganizationManagerPayload {
    access_level_id: number;
    owner: boolean;
}

export const updateOrganizationManagerThunk = (
    organizationManagerId: number,
    payload: UpdateOrganizationManagerPayload
): ThunkAction<Promise<OrganizationManagerUser>, RootState, null, OrganizationManagerAction> => {
    return async dispatch => {
        dispatch({ type: ORGANIZATION_MANAGER_SAVE_START });

        try {
            const url = `${organizationManagersUrl}/${organizationManagerId}`;
            const { data }: AxiosResponse<OrganizationManagerDto> = await ApiClient.patch(url, payload);

            const savedOrganizationManager = convertToOrganizationManagerUser(data);

            dispatch({
                type: ORGANIZATION_MANAGER_SAVE_SUCCESS,
                payload: {
                    organizationManager: savedOrganizationManager
                }
            });

            return savedOrganizationManager;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            return Promise.reject(apiError);
        }
    };
};

/**
 * Fetch Organization Managers - GET
 * Retrieves all organization managers for a specific organization
 */
export const fetchOrganizationManagersThunk = (
    organizationId: number
): ThunkAction<Promise<Array<OrganizationManagerUser>>, RootState, null, OrganizationManagerAction> => {
    return async dispatch => {
        dispatch({ type: ORGANIZATION_MANAGER_FETCH_START });

        try {
            const url = `list_managers/${organizationId}`;
            const { data }: AxiosResponse<FetchOrganizationManagersDto> = await ApiClient.get(url);

            // Convert the DTO response to OrganizationManagerUser model array
            const organizationManagers = Array.isArray(data.organization_users)
                ? data.organization_users.map(convertToOrganizationManagerUser)
                : [];

            dispatch({
                type: ORGANIZATION_MANAGER_FETCH_SUCCESS,
                payload: {
                    organizationManagers
                }
            });

            return organizationManagers;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            // Return rejected promise instead of throwing
            return Promise.reject(apiError);
        }
    };
};

/**
 * Delete Organization Manager - DELETE
 * Deletes an existing organization manager relationship
 */
export const deleteOrganizationManagerThunk = (
    organizationManagerId: number
): ThunkAction<Promise<void>, RootState, null, OrganizationManagerAction> => {
    return async dispatch => {
        dispatch({ type: ORGANIZATION_MANAGER_DELETE_START });

        try {
            const url = `${organizationManagersUrl}/${organizationManagerId}`;
            await ApiClient.delete(url);

            dispatch({
                type: ORGANIZATION_MANAGER_DELETE_SUCCESS,
                payload: {
                    organizationManagerId
                }
            });
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};
